import React from 'react';
import { Box } from '@mui/material';
import {
  CheckboxInput,
  ImageInput,
  RichTextInput,
  SelectInput,
  TextInput,
} from '@tassomutuo-backoffice/ui';
import composeImageUri from '../../utils/composeImageUri';
import { Controller, useFormContext } from 'react-hook-form';
import { Profile } from '@tassomutuo-backoffice/types';
import { PROVINCES } from './const/provinces';

const provincesSelectOptions = PROVINCES.map((p) => ({
  value: p.abbreviation,
  label: `${p.name} (${p.abbreviation})`,
}));

const createdBySelectOption = [
  { label: 'Backoffice', value: 'backoffice' },
  { label: 'Consulente', value: 'user' },
];

interface IFormInputs {
  email: string;
  name: string;
  lastName: string;
  provinces: string[];
  createdBy: string;
  profileImage: File | string | null;
  htmlDescription: string;
  published: boolean;
}

interface IProps {
  profileToUpdate: Profile | null;
  adminMode: boolean;
  disabled?: boolean;
}

const ProfileInputsForm = ({
  profileToUpdate,
  adminMode,
  disabled = false,
}: IProps) => {
  const {
    setValue,
    control,
    formState: { errors },
    watch,
  } = useFormContext<IFormInputs>();

  const isEditMode = !!profileToUpdate;

  return (
    <Box width="100%">
      <Box display="flex" gap={4}>
        <Box>
          <ImageInput
            disabledUpload={disabled}
            imageUrl={
              profileToUpdate?.tassomutuoProfile?.profileImageUrl
                ? composeImageUri(
                    profileToUpdate?.tassomutuoProfile.profileImageUrl
                  )
                : null
            }
            onChange={(imgData) => setValue('profileImage', imgData)}
          />

          {adminMode && (
            <Box mt={4}>
              <Controller
                disabled={disabled}
                control={control}
                name="published"
                defaultValue={false}
                shouldUnregister
                render={({ field }) => (
                  <CheckboxInput label="Pubblicato" {...field} />
                )}
              />
            </Box>
          )}
        </Box>
        <Box flex={1}>
          <Controller
            control={control}
            name="email"
            rules={{
              required: 'Campo obbligatorio',
            }}
            defaultValue=""
            disabled={isEditMode || disabled || !adminMode}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Email"
                helperText={errors?.email?.message}
                error={!!errors?.email}
                {...field}
              />
            )}
          />

          <Box display="flex" gap={2}>
            <Controller
              control={control}
              name="name"
              rules={{
                required: 'Campo obbligatorio',
              }}
              defaultValue=""
              disabled={disabled}
              render={({ field }) => (
                <TextInput
                  type="text"
                  label="Nome"
                  helperText={errors?.name?.message}
                  error={!!errors?.name}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="lastName"
              rules={{
                required: 'Campo obbligatorio',
              }}
              defaultValue=""
              disabled={disabled}
              render={({ field }) => (
                <TextInput
                  type="text"
                  label="Cognome"
                  helperText={errors?.lastName?.message}
                  error={!!errors?.lastName}
                  {...field}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2}>
            <Controller
              control={control}
              name="provinces"
              rules={{
                required: 'Campo obbligatorio',
              }}
              defaultValue={[]}
              disabled={disabled}
              render={({ field }) => (
                <SelectInput
                  label="Province"
                  helperText={errors?.provinces?.message}
                  error={!!errors?.provinces}
                  selectOptions={provincesSelectOptions}
                  multiple
                  {...field}
                />
              )}
            />
          </Box>
          {adminMode && (
            <Controller
              control={control}
              name="createdBy"
              rules={{
                required: 'Campo obbligatorio',
              }}
              defaultValue=""
              shouldUnregister
              disabled={disabled}
              render={({ field }) => (
                <SelectInput
                  label="Profilo creato da"
                  helperText={errors?.createdBy?.message}
                  error={!!errors?.createdBy}
                  selectOptions={createdBySelectOption}
                  {...field}
                />
              )}
            />
          )}
          {(watch('htmlDescription') || watch('htmlDescription') === '') && (
            <Controller
              control={control}
              name="htmlDescription"
              rules={{
                required: 'Campo obbligatorio',
              }}
              defaultValue=""
              disabled={disabled}
              render={({ field: { onChange, value, disabled } }) => (
                <RichTextInput
                  label="Descrizione"
                  helperText={errors?.htmlDescription?.message}
                  error={!!errors?.htmlDescription}
                  onChange={onChange}
                  value={value}
                  disabled={disabled}
                />
              )}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileInputsForm;
